import React, { ReactNode, useState, CSSProperties, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Box, useMediaQuery } from '@material-ui/core';
import useTheme from '@material-ui/core/styles/useTheme';
import { Color } from '@material-ui/lab';
import { FsComponentSize } from 'globals/types';
import defaultLocales from 'globals/locales/default';
import FsQuestionMarkContainedIcon from 'ui-elements/icons/FsQuestionMarkContained';
import FsIconButton from 'ui-elements/buttons/FsIconButton';
import FsDefaultDialog from 'ui-elements/modals/FsDefaultDialog';
import FsAlert from 'ui-elements/alerts/FsAlert';
import {
  ContentBox,
  ContainerBox,
  HeaderBox,
  MoreButton,
  SideHeaderBox,
  Title,
  SubTitle,
  TitleBox,
  SubHeaderBox,
} from './FsPaper.style-elements';
import {
  getAlertWrapperBoxStyle,
  getContainerStyle,
  getContentStyle,
  getLinkStyle,
  PaddingSizeType,
} from './FsPaper.style';

export interface FsPaperProps {
  children?: ReactNode;
  title?: string;
  subTitle?: ReactElement | string;
  tooltipContent?: ReactNode;
  link?: {
    label: string;
    url: string;
  };
  style?: CSSProperties;
  className?: string;
  paddingSize?: PaddingSizeType;
  withContentMargin?: boolean;
  withShadow?: boolean;
  buttons?: ReactElement[];
  severity?: Color;
  alertContent?: string | ReactElement;
}

const FsPaper: React.FC<FsPaperProps> = ({
  title,
  subTitle,
  tooltipContent,
  link,
  children,
  style,
  paddingSize,
  withContentMargin = true,
  withShadow = true,
  className,
  buttons,
  severity = 'info',
  alertContent,
}) => {
  // styles
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const contentStyle = getContentStyle(withContentMargin);
  const linkStyle = getLinkStyle();
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const subTitlePart =
    typeof subTitle === 'string' ? <SubTitle>{subTitle}</SubTitle> : subTitle;
  // const period = `${formatDate(new Date().getTime(), true)} - ${formatDate(
  //   new Date().getTime(),
  //   true
  // )}`;

  // parts
  // const timePeriodLabelPart = period && (
  //   <TimePeriodTooltip
  //     title={`Scores presented inside this container relates to time period: ${period}`}
  //     arrow
  //   >
  //     <TimePeriodContentBox>
  //       <TimePeriodIcon />
  //       <TimePeriodLabel>{period}</TimePeriodLabel>
  //     </TimePeriodContentBox>
  //   </TimePeriodTooltip>
  // );

  const handleOnClose = (): void => {
    setIsOpenDialog(false);
  };

  const handleClick = (): void => {
    setIsOpenDialog(true);
  };

  const tooltipPart = tooltipContent && (
    <>
      <FsIconButton
        aria-label={defaultLocales.moreInfo}
        size="small"
        onClick={handleClick}
        data-test="tooltip-dialog-button"
      >
        <FsQuestionMarkContainedIcon />
      </FsIconButton>
      <FsDefaultDialog
        isOpen={isOpenDialog}
        title={title}
        onClose={handleOnClose}
      >
        {tooltipContent}
      </FsDefaultDialog>
    </>
  );

  const linkLabel = isUpSm ? link?.label : defaultLocales.more;
  const isLink = linkLabel && link?.url;
  const linkPart = isLink && (
    <Link to={link?.url || ''} style={linkStyle}>
      <MoreButton>{linkLabel}</MoreButton>
    </Link>
  );

  const alertPart = alertContent && (
    <Box marginBottom={2}>
      <FsAlert severity={severity}>{alertContent}</FsAlert>
    </Box>
  );

  const containerStyle = getContainerStyle({
    style,
    paddingSize: paddingSize || FsComponentSize.M,
    withShadow,
    theme,
  });

  const alertWrapperBoxStyle = getAlertWrapperBoxStyle({
    ...theme,
    severity,
    isContent: !!alertContent,
  });

  return (
    <ContainerBox style={containerStyle} className={className}>
      <HeaderBox>
        <TitleBox>{title && <Title>{title}</Title>}</TitleBox>
        <SideHeaderBox>
          {tooltipPart}
          {linkPart}
          {buttons}
        </SideHeaderBox>
      </HeaderBox>
      {subTitle && <SubHeaderBox>{subTitlePart}</SubHeaderBox>}
      <ContentBox style={contentStyle}>
        {alertPart}
        <Box style={alertWrapperBoxStyle}>{children}</Box>
      </ContentBox>
    </ContainerBox>
  );
};

export default FsPaper;
